import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, Download, FileSpreadsheet, Trash2, Upload, X } from 'lucide-react'
import { Button, showToastMessage } from '../../../../components'
import {
  downloadAttendanceImportTemplate,
  downloadBlob,
  importAttendanceRecords,
  type ImportAttendanceResponse,
} from '../services/attendanceService'

interface ImportAttendanceModalProps {
  open: boolean
  onClose: () => void
  onSuccessRefetch?: () => Promise<{ count: number; total: number } | any> | void
  onImport?: (records: any[]) => void // Retained for backwards compatibility if needed
}

export function ImportAttendanceModal({
  open,
  onClose,
  onSuccessRefetch,
}: ImportAttendanceModalProps) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState<boolean>(false)
  const [importResult, setImportResult] = useState<ImportAttendanceResponse | null>(null)

  const dialogRef = useRef<HTMLDivElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)

  // ESC key listener & body lock
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleModalClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    const origOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = origOverflow
    }
  }, [open])

  const handleModalClose = () => {
    if (isUploading) return
    setUploadedFile(null)
    setImportResult(null)
    onClose()
  }

  // 4) DOWNLOAD TEMPLATE: GET /records/import/template
  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true)
    try {
      const res = await downloadAttendanceImportTemplate()
      downloadBlob(res.data, res.filename || 'attendance-import-template.xlsx')
      showToastMessage('Attendance template downloaded successfully', 'success')
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Failed to download import template'
      showToastMessage(msg, 'error')
    } finally {
      setIsDownloadingTemplate(false)
    }
  }

  const handleFileValidationAndSet = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!ext || !['xlsx', 'xls', 'csv'].includes(ext)) {
      showToastMessage('Please select a valid .xlsx, .xls, or .csv file', 'error')
      return
    }
    setUploadedFile(file)
    setImportResult(null)
  }

  const handleFileSelect = (file: File) => {
    handleFileValidationAndSet(file)
  }

  const handleRemoveFile = () => {
    setUploadedFile(null)
    setImportResult(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileValidationAndSet(e.dataTransfer.files[0])
    }
  }

  // 5) IMPORT: POST /records/import
  const handleSubmit = async () => {
    if (!uploadedFile) return
    setIsUploading(true)
    try {
      const res = await importAttendanceRecords(uploadedFile)
      setImportResult(res)

      const isErrorOnly = res.failed > 0 && res.created === 0 && res.updated === 0
      showToastMessage(
        `Import summary: ${res.created} created, ${res.updated} updated, ${res.failed} failed`,
        isErrorOnly ? 'error' : 'success'
      )

      let refetchResult: any = undefined
      if (onSuccessRefetch) {
        refetchResult = await onSuccessRefetch()
      }

      // Findability hint: if successful import produced/updated records but current filtered view has 0 rows
      if (res.created + res.updated > 0) {
        const loadedCount =
          typeof refetchResult?.count === 'number'
            ? refetchResult.count
            : typeof refetchResult?.total === 'number'
            ? refetchResult.total
            : undefined

        if (loadedCount === 0) {
          showToastMessage(
            'Imported records are dated outside the selected date. Change or clear the date filter to view them.',
            'info'
          )
        }
      }

      // If clean import with no errors, close modal
      if (!res.errors || res.errors.length === 0) {
        setUploadedFile(null)
        onClose()
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Failed to import attendance file'
      showToastMessage(msg, 'error')
    } finally {
      setIsUploading(false)
    }
  }

  if (!open) return null

  const modalContent = (
    <div
      aria-modal="true"
      role="dialog"
      aria-labelledby="import-attendance-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 font-sans"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleModalClose()
        }
      }}
    >
      <div
        ref={dialogRef}
        className="w-[560px] max-w-[92vw] bg-white rounded-2xl shadow-2xl p-6 relative flex flex-col mx-auto my-auto max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between pb-4 border-b border-[#E2E8F0] gap-4 w-full">
          <h2
            id="import-attendance-modal-title"
            className="text-lg sm:text-xl font-bold text-[#0F172A] whitespace-nowrap"
          >
            Import Staff Attendance
          </h2>

          <div className="flex items-center gap-3 shrink-0 ml-auto">
            <button
              type="button"
              disabled={isDownloadingTemplate}
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0F766E] hover:text-[#0d645e] hover:underline transition cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              {isDownloadingTemplate ? 'Downloading...' : 'Download Template'}
            </button>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={handleModalClose}
              aria-label="Close dialog"
              className="rounded-full p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition cursor-pointer shrink-0 ml-auto"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Upload Area */}
        <div className="w-full my-5 space-y-4">
          <label
            onDragEnter={handleDragLeave}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`group relative flex flex-col items-center justify-center w-full min-h-[200px] rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition duration-200 ${
              dragActive
                ? 'border-[#0F766E] bg-[#E8F7F1]'
                : 'border-slate-300 bg-[#F8FAFC] hover:border-[#0F766E] hover:bg-slate-50'
            }`}
          >
            <input
              type="file"
              accept=".csv, .xlsx, .xls"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  handleFileSelect(e.target.files[0])
                }
              }}
            />
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E8F7F1] text-[#0F766E] mb-3 group-hover:scale-105 transition-transform shrink-0">
              <Upload className="h-6 w-6" />
            </div>
            <p className="text-sm font-bold text-[#0F172A] whitespace-nowrap">
              Click to upload or drag and drop
            </p>
            <div className="mt-2 text-xs text-[#64748B] space-y-0.5">
              <p>
                Supported formats:{' '}
                <span className="font-semibold text-[#0F172A]">.xlsx, .xls, .csv</span>
              </p>
              <p>
                Maximum size: <span className="font-semibold text-[#0F172A]">10MB</span>
              </p>
            </div>
          </label>

          {/* Selected File Details */}
          {uploadedFile && (
            <div className="flex items-center justify-between w-full rounded-lg border border-[#B4ECF2] bg-[#E8F7F1] p-3 text-sm text-[#0F172A]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0F766E] text-white">
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{uploadedFile.name}</p>
                  <p className="text-xs text-[#64748B]">
                    {(uploadedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:underline cursor-pointer shrink-0 ml-3"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          )}

          {/* Import Result Summary & Errors */}
          {importResult && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-slate-700">Import Processing Summary:</span>
                <span className="text-[#0F766E]">
                  {importResult.created} created · {importResult.updated} updated ·{' '}
                  <span className={importResult.failed > 0 ? 'text-red-600 font-bold' : ''}>
                    {importResult.failed} failed
                  </span>
                </span>
              </div>

              {importResult.errors && importResult.errors.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-slate-200">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-red-700">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>Errors Encountered ({importResult.errors.length}):</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1.5 rounded-lg border border-red-200 bg-red-50/70 p-2.5 text-xs text-red-800">
                    {importResult.errors.map((err, idx) => (
                      <div key={idx} className="flex items-start gap-1.5">
                        <span className="font-semibold shrink-0">
                          {err.row ? `Row ${err.row}:` : '•'}
                        </span>
                        {err.employeeCode && (
                          <span className="font-mono bg-red-100 px-1 rounded shrink-0">
                            [{err.employeeCode}]
                          </span>
                        )}
                        <span>{err.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pt-4 border-t border-[#E2E8F0] flex flex-row items-center justify-between w-full">
          <Button variant="secondary" onClick={handleModalClose} disabled={isUploading}>
            {importResult && importResult.errors?.length ? 'Close' : 'Cancel'}
          </Button>

          <Button
            variant="primary"
            disabled={!uploadedFile || isUploading}
            onClick={handleSubmit}
            className="bg-[#0F766E] hover:bg-[#0d645e] text-white"
          >
            {isUploading ? 'Importing...' : 'Upload Attendance'}
          </Button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
